import { Reducer } from 'redux';
import { ActionType, getType } from 'typesafe-actions';
import { sendEvent, track } from '../../Utility/Tracking/Track';
import {
    MissionTrackingEventName,
    TransactionTrackingEventName,
} from '../../Utility/Tracking/TrackingEvent';
import { CardActions } from '../Card/CardActions';
import { cardReducer } from '../Card/CardReducer';
import { getGoldCardsInPlay } from '../Card/CardStateHelper';
import FTUXStateHelper from '../Game/FTUXStateHelper';
import { IGameState } from '../Game/IGameState';
import { setExtraCards } from '../Player/PlayerInventoryHelper';
import { levelComplete } from '../Player/PlayerLevelHelper';
import { clearTasks } from '../Player/PlayerStateHelper';
import { showPopup } from '../UI/UIStateHelper';
import { HelperFriendRequestReducer } from './HelperFriend/HelperFriendReducer';
import { checkHelperFriendVisible } from './HelperFriend/HelperFriendStateHelper';
import { LevelStatus, WinCriteriaType } from './ILevelState';
import { LevelActions } from './LevelActions';
import LevelStateHelper from './LevelStateHelper';

export const LevelReducer: Reducer<IGameState> = (
    state: IGameState,
    action: ActionType<typeof LevelActions | typeof CardActions>
): IGameState => {
    switch (action.type) {
        case getType(LevelActions.setLevelState):
            if (state.levelState.status === LevelStatus.LOADING) {
                state = {
                    ...state,
                    levelState: {
                        ...state.levelState,
                        ...action.payload,
                        status: LevelStatus.LOADED,
                    },
                    playerState: setExtraCards(state.playerState, 0),
                };

                // FTUX tasks and level unlocks.
                state = FTUXStateHelper.setLevelTasks(state);
                state = FTUXStateHelper.setLevelComponents(state);

                track('mGoalAmount', state.levelState.cardsState.inPlay.length);
                track(
                    'mLevelMoves',
                    state.levelState.cardsState.inStack.length
                );

                const level = state.playerState.levels[
                    state.levelState.levelType
                ].find(l => l.levelNumber === state.levelState.levelNumber);
                track('mStars', level ? level.stars : 0);
            }
            break;
        case getType(LevelActions.levelIntro):
            if (state.levelState.status === LevelStatus.LOADED) {
                state = {
                    ...state,
                    levelState: {
                        ...state.levelState,
                        status: LevelStatus.INTRO,
                    },
                };
            }
            break;
        case getType(LevelActions.levelActive):
            if (state.levelState.status === LevelStatus.INTRO) {
                state = {
                    ...state,
                    levelState: {
                        ...state.levelState,
                        status: LevelStatus.ACTIVE,
                        // tasks: [] /* Clear level/FTUX tasks. */,

                        // Reset undo history/count at the start of each level.
                        cardsState: {
                            ...state.levelState.cardsState,
                            undoHistory: [],
                        },
                    },
                };
                // track('tLevelExtraCardsNumber', 0);
                track('lastLevelAttempted', state.levelState.levelNumber);
                sendEvent(MissionTrackingEventName.missionStarted, state);
            }
            break;
        case getType(LevelActions.levelEnd): {
            if (state.levelState.status === LevelStatus.ACTIVE) {
                track(
                    'mGoalsCompletedPercentage',
                    state.levelState.cardsState.inPlay.length
                );

                track('mCardsLeft', state.levelState.cardsState.inPlay.length);
                track('mScore', state.levelState.score);

                state = {
                    ...state,
                    levelState: {
                        ...state.levelState,
                        status: LevelStatus.FAILED,
                    },
                    UIState: showPopup(state.UIState, 'levelCompleteMenu', {
                        levelNumber: state.levelState.levelNumber,
                        fail: true,
                        goals: state.levelState.winCriteria,
                    }),
                };
            }
            break;
        }
        case getType(LevelActions.setLevelLeaderboard):
            state = {
                ...state,
                levelState: {
                    ...state.levelState,
                    leaderboard: action.payload,
                },
            };
            break;
    }

    if (state.levelState.status === LevelStatus.ACTIVE) {
        const previousState = { ...state };

        // Handle active level state actions.
        state = cardReducer(state, action);

        state = HelperFriendRequestReducer(state, action);

        state = checkHelperFriendVisible(state);

        // Update clear cards, jokers and streaks missions.
        state = LevelStateHelper.updateMissionProgress(state, previousState);

        // Clear "player" tasks when the level is active to prevent various tasks queuing and playing once returned
        // to the world scene.
        // TODO: Handle tasks in the level scene.
        state = {
            ...state,
            playerState: clearTasks(state.playerState),
        };

        if (isLevelComplete(state)) {
            track('mScoreBeforeEndSequence', state.levelState.score);

            state = LevelStateHelper.levelComplete(state);

            state = levelComplete(
                state,
                state.levelState.levelNumber,
                state.levelState.levelType,
                state.levelState.completeData.stars,
                state.levelState.completeData.coins
            );

            track('mIsSuccessfulRetry', state.levelState.attempts > 1 ? 1 : 0);
            track('mStars', state.levelState.completeData.stars);
            track('mScore', state.levelState.completeData.score);
            track('lastLevelWon', state.levelState.levelNumber);
            sendEvent(MissionTrackingEventName.missionCompleted, state);
            sendEvent(TransactionTrackingEventName.resource, state);
        }
    }

    return state;
};

/**
 * Check for level complete.
 * @param state
 */
function isLevelComplete(state: IGameState): boolean {
    return state.levelState.winCriteria.every(criteria => {
        switch (criteria.type) {
            case WinCriteriaType.CLEAR_ALL:
                return state.levelState.cardsState.inPlay.length === 0;
            case WinCriteriaType.CLEAR_GOLD:
                return getGoldCardsInPlay(state.levelState.cardsState) === 0;
        }
    });
}
